import { flow, getEnv, Instance } from "mobx-state-tree";

import { JobInput } from "../../api-models/request/JobInput";
import { multipleItemPatch } from "../api/ApiService";
import { Job } from "./Job";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { JobType, JobPriority, JobStatus } from "../../generated/Jobs";

export const JobsStorageModel = DataGridStorageModel.named("JobsStorageModel")
  .actions(() => {
    return {
      getTranslatedJobType(type: JobType) {
        return `general.jobs.type.${Object.keys(JobType).find((key) => JobType[key] === type)}`;
      },

      getTranslatedJobPriority(priority: JobPriority) {
        return `general.jobs.priority.${Object.keys(JobPriority).find(
          (key) => JobPriority[key] === priority
        )}`;
      },

      getTranslatedJobStatus(status: JobStatus) {
        return `general.jobs.status.${Object.keys(JobStatus).find(
          (key) => JobStatus[key] === status
        )}`;
      },
    };
  })
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "general.name",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "status",
          name: "general.status",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedJobStatus(JobStatus.ToDo),
              value: JobStatus.ToDo.toString(),
            },
            {
              label: self.getTranslatedJobStatus(JobStatus.InProgress),
              value: JobStatus.InProgress.toString(),
            },
            {
              label: self.getTranslatedJobStatus(JobStatus.Done),
              value: JobStatus.Done.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "type",
          name: "general.type",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedJobType(JobType.DataGathering),
              value: JobType.DataGathering.toString(),
            },
            {
              label: self.getTranslatedJobType(JobType.VideoInspection),
              value: JobType.VideoInspection.toString(),
            },
            {
              label: self.getTranslatedJobType(JobType.VideoQA),
              value: JobType.VideoQA.toString(),
            },
            {
              label: self.getTranslatedJobType(JobType.Other),
              value: JobType.Other.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "priority",
          name: "general.priority",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedJobPriority(JobPriority.Low),
              value: JobPriority.Low.toString(),
            },
            {
              label: self.getTranslatedJobPriority(JobPriority.Medium),
              value: JobPriority.Medium.toString(),
            },
            {
              label: self.getTranslatedJobPriority(JobPriority.High),
              value: JobPriority.High.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "deadline",
          name: "general.deadline",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "assignedUser",
          name: "general.jobs.list-header.assigned",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "project",
          name: "general.project",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "createdBy",
          name: "general.jobs.list-header.created-by",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "modified",
          name: "general.last-edit",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
        self.gridColumns = self.tableColumns;
      },

      mapItem(item: any) {
        return new Job(item);
      },

      createJob: flow(function* (job: JobInput) {
        const endpoint = endpoints.get("jobs");
        return yield getEnv(self).api.post(endpoint, job);
      }),

      deleteJobs: flow(function* (ids) {
        const endpoint = endpoints.get("jobs");
        const response = yield getEnv(self).api.delete(endpoint, ids);
        self.deselectAll();
        yield self.load();
        return response;
      }),

      changeJobsStatus: flow(function* (ids, status) {
        const patchData: multipleItemPatch = {
          ids,
          jobPatch: [
            {
              op: "replace",
              path: "/status",
              value: status,
            },
          ],
        };
        const endpoint = endpoints.get("jobs");
        const result = yield getEnv(self).api.patch(endpoint, patchData);
        self.deselectAll();
        yield self.load();
        return result;
      }),

      changeJobsPriority: flow(function* (ids, priority) {
        const patchData: multipleItemPatch = {
          ids,
          jobPatch: [
            {
              op: "replace",
              path: "/priority",
              value: priority,
            },
          ],
        };
        const endpoint = endpoints.get("jobs");
        const result = yield getEnv(self).api.patch(endpoint, patchData);
        self.deselectAll();
        yield self.load();
        return result;
      }),

      addToProject: flow(function* (ids, projectId) {
        const patchData: multipleItemPatch = {
          ids,
          jobPatch: [
            {
              op: "replace",
              path: "/projectId",
              value: projectId,
            },
          ],
        };
        const endpoint = endpoints.get("jobs");
        const result = yield getEnv(self).api.patch(endpoint, patchData);
        self.deselectAll();
        yield self.load();
        return result;
      }),

      assignUserToJobs: flow(function* (ids, userId) {
        const patchData: multipleItemPatch = {
          ids,
          jobPatch: [
            {
              op: "replace",
              path: "/assignedUserId",
              value: userId,
            },
          ],
        };
        const endpoint = endpoints.get("jobs");
        const result = yield getEnv(self).api.patch(endpoint, patchData);
        self.deselectAll();
        yield self.load();
        return result;
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("jobs_all");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
  }));

export type JobsStorage = Instance<typeof JobsStorageModel>;
